import { Building2, ChevronsUpDown } from "lucide-react";
import { Suspense, useState } from "react";
import { LazyIdentitySwitcherDropdown } from "./LazyIdentitySwitcherDropdown";
import { useNavigationTransition } from "./useNavigationTransition";
import {
    DropdownMenu,
    DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarProvider
} from "@/components/ui/sidebar";
import { useParallel } from "@/frontend/@api/useParallel";
import { Loader } from "@/frontend/@ui/Loader";
import { reexported__useCurrentFirm } from "@/frontend/firm/api/firmGetters";
import { reexported__useCurrentUser } from "@/frontend/user/api/userGetters";

export function IdentitySwitcher() {
    const [user, firm] = useParallel(
        () => reexported__useCurrentUser(),
        () => reexported__useCurrentFirm()
    );

    const [isMenuOpen, setIsMenuOpenInternal] = useState(false);
    // hasMenuEverOpened is used to ensure that IdentitySwitcherDropdown stays mounted even if it is closed, to allow for close animation to complete
    // but we only want the component to be mounted if it has ever been opened so as not to make api calls unless needed
    const [hasMenuEverOpened, setHasMenuEverOpened] = useState(false);
    const setIsMenuOpen = (value: boolean) => {
        setIsMenuOpenInternal(value);
        if (value) {
            setHasMenuEverOpened(true);
        }
    };

    const { isPending, spread } = useNavigationTransition({
        onTransition: () => {
            setIsMenuOpen(true);
        },
        preloadElement: {
            key: "IdentitySwitcherDropdown",
            element: (
                <SidebarProvider>
                    <DropdownMenu>
                        <LazyIdentitySwitcherDropdown
                            closeMenu={() => undefined}
                        />
                    </DropdownMenu>
                </SidebarProvider>
            )
        }
    });

    const onOpenChange = (open: boolean) => {
        if (open) {
            spread.onClick(undefined);
        } else {
            setIsMenuOpen(false);
        }
    };

    return (
        <SidebarMenu>
            <SidebarMenuItem>
                <DropdownMenu open={isMenuOpen} onOpenChange={onOpenChange}>
                    <DropdownMenuTrigger asChild>
                        <SidebarMenuButton
                            size="lg"
                            className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                            {...spread}
                        >
                            <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                                <Building2 className="size-4" />
                            </div>
                            <div className="grid flex-1 text-left text-sm leading-tight">
                                <span className="truncate font-semibold">
                                    {firm.name}
                                </span>
                                <span className="truncate text-xs">
                                    {user.firstName} {user.lastName}
                                </span>
                            </div>
                            {isPending ? (
                                <Loader className="ml-auto" />
                            ) : (
                                <ChevronsUpDown className="ml-auto" />
                            )}
                        </SidebarMenuButton>
                    </DropdownMenuTrigger>

                    <Suspense>
                        {(isMenuOpen || hasMenuEverOpened) && (
                            <LazyIdentitySwitcherDropdown
                                closeMenu={() => {
                                    setIsMenuOpen(false);
                                }}
                            />
                        )}
                    </Suspense>
                </DropdownMenu>
            </SidebarMenuItem>
        </SidebarMenu>
    );
}
